import { d3, cropr, gettext } from "../adapter";
import extend from "lodash/extend";
import moment from "moment";
import timeFormat from "./chartTimeFormat";
import weatherStationActions from "./../actions/weatherStationActions";

// For the tooltip
var bisectDate = d3.bisector(function (d) {
  return d.timestamp;
}).left;

// The constructor
class Chart {
  constructor(options, data, dispatch) {
    // Empty data, this is our data format
    this.data = { values: [] };
    this.dispatch = dispatch;
    // Default options
    this.options = {
      id: false,
      class: false,
      width: 500,
      height: 350,
      i18n: {
        unit: cropr.l10n.unit_temp_display, //I18n.t('units.degrees.short'),
        unitBig: cropr.l10n.unit_temp_display, // I18n.t('units.degrees.long'),
      },
    };

    // Merge the options
    this.options = extend(this.options, options);

    // Set chart dimension, also depending on settings as brush
    var margin = { top: 10, right: 70, bottom: 30, left: 70 };
    var height = this.options.height - (margin.top + margin.bottom);
    var width = this.options.width - (margin.left + margin.right);

    this.chart = {
      width: width,
      height: height,
      margin: margin,
    };

    this.totalChart = {
      height: this.chart.height,
      margin: {
        top: this.chart.margin.top,
      },
    };

    // Set the data
    if (data) {
      this.setData(data);
    }

    // No SVG and elements yet
    this.elements = {};

    var _this = this;
    var scrolling = false;
    var x;
    this.zoom = d3.behavior
      .zoom()
      .x(this.scales.x)
      .on("zoom", function () {
        if (_this.scales.x.domain()[0] < _this.data.minDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.minDate) +
            _this.scales.x.range()[0];
          _this.zoom.translate([x, 0]);
        } else if (_this.scales.x.domain()[1] > _this.data.maxDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.maxDate) +
            _this.scales.x.range()[1];
          _this.zoom.translate([x, 0]);
        }

        // Execute only once
        if (!scrolling) {
          _this.elements.eventRect.classed("grabbing", true);
          _this.dispatch(weatherStationActions.hovered(false));
        }

        scrolling = true;
        _this._scroll();
      })
      .on("zoomend", function () {
        if (!scrolling) return;

        _this.elements.eventRect.classed("grabbing", false);

        // Enable the pin hovers again when we stop zooming
        if (this === d3.event.sourceEvent.target) {
          _this.dispatch(weatherStationActions.hovered(true));
        }

        scrolling = false;
        _this.dispatch(
          weatherStationActions.brushDomain([
            _this.scales.x.domain()[0],
            _this.scales.x.domain()[1],
          ])
        );
      });
  }
}

// Init graph
Chart.prototype.init = function (el) {
  var _this = this;

  // Only init once and only init if we have data
  if (this.svg || !this.data) {
    return false;
  }

  // Create the SVG
  this.elements.svg = d3
    .select(el)
    .append("svg")
    .attr("class", this.options.class)
    .attr("id", this.options.id)
    .attr("width", this.options.width)
    .attr("height", this.options.height);

  var chart = (this.elements.chart = this.elements.svg
    .append("g")
    .attr("class", "chart")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")"));

  this.elements.innerChart = this.elements.svg
    .append("g")
    .attr("class", "inner")
    .attr("clip-path", "url(#clip-" + this.options.id + ")")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")");

  this.elements.grid = {
    x: chart
      .append("g")
      .attr("class", "grid x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "grid y"),
  };

  this.elements.legendList = d3
    .select(el)
    .append("ul")
    .attr("class", "l-stacked legend has-left-spacing");
  this.elements.legendLine = this.elements.legendList.append("li").attr("class", "legend first");
  this.elements.legendLine.append("span").attr("class", "airTemperature line").html("&nbsp;");
  this.elements.legendLine
    .append("span")
    .attr("class", "content")
    .html(
      gettext("Air temperature weather in {{unit}}").replace("{{unit}}", this.options.i18n.unitBig)
    );
  if (this.data.station.measurements.dewPoint) {
    this.elements.legendLine = this.elements.legendList.append("li").attr("class", "legend");
    this.elements.legendLine.append("span").attr("class", "precipChance line").html("&nbsp;");
    //      this.elements.legendLine.append('span').attr('class', 'content').html('Dewpoint in '+this.options.i18n.unitBig);
    this.elements.legendLine
      .append("span")
      .attr("class", "content")
      .html(gettext("Dewpoint in {{unit}}").replace("{{unit}}", this.options.i18n.unitBig));
  }

  this.elements.visuals = this.elements.innerChart
    .append("g")
    .attr("class", "visuals")
    .attr("mask", "url(#mask-" + this.options.id + ")");

  // Draw the sum water line
  this.elements.line = this.elements.innerChart.append("path").attr("class", "line airTemperature");
  if (this.data.station.measurements.dewPoint) {
    this.elements.linedp = this.elements.innerChart
      .append("path")
      .attr("class", "line airTemperature");
    this.elements.linedp.style("stroke-width", 1);
    this.elements.linedp.style("stroke", "#00FF00");
  }

  this.elements.chartCover = this.elements.svg
    .append("g")
    .attr("class", "cover")

    // .attr('clip-path', 'url(#clip-' + this.options.id + ')')
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    );

  // Draw the mouse vertical line
  var clipId = "clip";
  if (this.options.hasPrecipitation) {
    clipId = "clip-total";
  }

  // Draw the mouse vertical line
  this.elements.mouse = this.elements.chartCover
    .append("g")
    .attr("clip-path", "url(#" + clipId + "-" + this.options.id + ")")
    .append("g")
    .attr("class", "mouse")
    .style("opacity", "0");

  this.elements.mouse
    .append("line")
    .attr("class", "line mouse")
    .attr("x1", 2)
    .attr("y1", 8)
    .attr("x2", 2)
    .attr("y2", this.totalChart.height);

  this.elements.mouse
    .append("circle")
    .attr("class", "circle mouse")
    .attr("cx", 2)
    .attr("cy", 0)
    .attr("r", 6);

  this.elements.tooltip = this.elements.mouse.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipBalloon = this.elements.tooltip.insert("rect").attr("rx", 2).attr("ry", 2);

  //  this.elements.tooltipText = this.elements.tooltip.append('text').attr('class', 'tooltipText');
  this.elements.tooltip1 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipText1 = this.elements.tooltip1.append("text").attr("class", "tooltipText");

  this.elements.tooltip2 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipText2 = this.elements.tooltip2.append("text").attr("class", "tooltipText");

  // Draw the axis
  this.elements.axis = {
    x: chart
      .append("g")
      .attr("class", "axis x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "axis y"),
  };

  this.elements.eventRect = this.elements.svg
    .append("svg:rect")
    .attr("class", "rect event")
    .attr("width", this.chart.width)
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    )
    .attr("height", this.totalChart.height)
    .attr("fill", "none")
    .attr("pointer-events", "all")
    .on("mouseout", function () {
      // on mouse out hide line, circles and text
      _this.dispatch(weatherStationActions.hovered(false));
    })
    .on("mouseover", function () {
      // on mouse in show line, circles and text
      _this.dispatch(weatherStationActions.hovered(true));
    })
    .on("mousemove", function () {
      // mouse moving over canvas
      //if(d3.event.which) return;
      // var data = _this.data.station.measurements.airTemperature;
      var mouseX = d3.mouse(this)[0]; // mouse position in x
      var x0 = _this.scales.x.invert(mouseX); // date corresponding to mouse x

      _this.dispatch(weatherStationActions.hoverDate(x0));
    })
    .on("click", function () {
      if (d3.event.defaultPrevented) return;
      _this.dispatch(weatherStationActions.togglePin());
    })
    .call(this.zoom)
    .on("wheel.zoom", null)
    .on("MozMousePixelScroll.zoom", null)
    .on("mousewheel.zoom", null)
    .on("dblclick.zoom", null);

  // Draw the clippaths
  this._drawClipPaths();
  this.update();
};

// Set the data we need, we expect to receive it in the correct format
Chart.prototype.setData = function (data) {
  // Set the data
  this.data = extend(this.data, data);

  // Set min and max data value
  this.data.minDate = this.data.station.measurements.airTemperature[0].timestamp;
  this.data.maxDate =
    this.data.station.measurements.airTemperature[
      this.data.station.measurements.airTemperature.length - 1
    ].timestamp;

  // Create or update scales and axis etc.
  this._setScales();
  this._setAxis();

  this._setDataShapes();
};

Chart.prototype.updateHoverState = function (hovered, pinned, pinDate) {
  if (hovered && !pinned) {
    this.elements.mouse.style("opacity", ".5");
    this.elements.eventRect.classed("grab", false);
  } else if (!pinned) {
    this.elements.mouse.style("opacity", "0");
  } else {
    this.data.pinDate = pinDate;
    this.elements.mouse.style("opacity", "1");
    this.elements.eventRect.classed("grab", true);
  }

  this.data.pinned = pinned;
};

Chart.prototype.updateHoverDate = function (date, pinned) {
  if (!pinned) {
    var xPos = this.scales.x(date);
    this.elements.mouse.attr("transform", "translate(" + (xPos - 2) + "," + 0 + ")");

    var data = this.data.station.measurements.airTemperature;
    var i = bisectDate(data, date, 1);
    var d0 = data[i - 1];
    var d1 = data[i];
    var d = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;
    var dtool = new Date(date);

    if (date) {
      dtool.setMinutes(Math.floor(date.getMinutes() / 15) * 15);
    }
    this.elements.tooltipText1.text(moment(dtool).format("L LT"));
    this.elements.tooltipText2.text(
      cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(d.y), 1) +
        " " +
        cropr.l10n.unit_temp_display
    );

    // Calculate the bbox
    var tooltipMarginLeftRight = 5; // 5px margin
    var tooltipMarginTopBottom = 4; // 5px margin

    if (xPos < this.chart.width - this.chart.margin.right - 20) {
      this.elements.tooltip1.attr("transform", "translate(21, 9)");
      this.elements.tooltipText1.attr("text-anchor", "left");
      this.elements.tooltip2.attr("transform", "translate(21, 24)");
      this.elements.tooltipText2.attr("text-anchor", "left");
    } else {
      this.elements.tooltip1.attr("transform", "translate(-20, 9)");
      this.elements.tooltipText1.attr("text-anchor", "end");
      this.elements.tooltip2.attr("transform", "translate(-20, 24)");
      this.elements.tooltipText2.attr("text-anchor", "end");
    }

    var bbox1 = this.elements.tooltipText1[0][0].getBBox();
    var bbox2 = this.elements.tooltipText2[0][0].getBBox();

    if (bbox1.width > bbox2.width) {
      var bboxWidth = bbox1.width + 2 * tooltipMarginLeftRight;
      var bboxX = bbox1.x;
    } else {
      bboxWidth = bbox2.width + 2 * tooltipMarginLeftRight;
      bboxX = bbox2.x;
    }

    this.elements.tooltipBalloon
      .attr("y", bbox1.y - tooltipMarginTopBottom + 9)
      .attr("width", bboxWidth)
      .attr("height", bbox1.height * 2 + 2 * tooltipMarginTopBottom);

    if (xPos < this.chart.width - this.chart.margin.right - 20) {
      this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight + 20);
    } else {
      this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight - 20);
    }
  }
};

// Update
Chart.prototype.update = function (data) {
  if (data) {
    // DIE SETDATA FUNCTIE BETER BEKIJKEN
    this.setData(data);
    this.zoom.x(this.scales.x);
    this.scales.x.domain(data.domain);
  }

  var airTemperature = this.data.station.measurements.airTemperature;
  var dewPoint = this.data.station.measurements.dewPoint;

  // Update the line graph data
  this.elements.line.datum(airTemperature);
  if (dewPoint) {
    this.elements.linedp.datum(dewPoint);
    this.elements.linedp.transition().attr("d", this.shapes.line);
  }

  this.elements.axis.x.transition().call(this.axis.x);
  this.elements.axis.y.transition().call(this.axis.y);
  this.elements.grid.x.transition().call(this.axis.gridX);
  this.elements.grid.y.transition().call(this.axis.gridY);

  this.elements.line.transition().attr("d", this.shapes.line);

  this.elements.eventRect.attr("width", this.chart.width);

  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);

  if (this.data.pinned) {
    this.elements.mouse
      .transition()
      .attr("transform", "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")");
  }
};

// Not really DRY. The same as update besides the transitions
Chart.prototype._scroll = function () {
  var airTemperature = this.data.station.measurements.airTemperature;

  // Update the line graph data
  this.elements.line.datum(airTemperature);

  this.elements.axis.x.call(this.axis.x);
  this.elements.axis.y.call(this.axis.y);
  this.elements.grid.x.call(this.axis.gridX);
  this.elements.grid.y.call(this.axis.gridY);

  this.elements.line.attr("d", this.shapes.line);

  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);

  if (this.data.pinned) {
    this.elements.mouse.attr(
      "transform",
      "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")"
    );
  }
};

Chart.prototype.resize = function (width, height) {
  if (width) {
    this.options.width = width;
    this.chart.width = this.options.width - (this.chart.margin.left + this.chart.margin.right);
  }

  if (height) {
    this.options.height = height;
    this.chart.width = this.options.height - (this.chart.margin.top + this.chart.margin.bottom);
  }

  this.elements.svg.style("width", this.options.width);
  this.elements.svg.style("height", this.options.height);

  // Update the scales
  this._setScales();
  this._setAxis();
  this.update();
};

Chart.prototype.destroy = function () {
  this.elements.svg.remove();
};

Chart.prototype._setScales = function () {
  var domain = this.data.domain;

  var maxYDomain = d3.max(
    this.data.station.measurements.airTemperature.map(function (d) {
      return d.y;
    })
  );

  var minYDomain = d3.min(
    this.data.station.measurements.airTemperature.map(function (d) {
      return d.y;
    })
  );
  if (this.data.station.measurements.dewPoint) {
    var minDPDomain = d3.min(
      this.data.station.measurements.dewPoint.map(function (d) {
        return d.y;
      })
    );
    if (minDPDomain < minYDomain) {
      minYDomain = minDPDomain;
    }
  }
  if (!maxYDomain) maxYDomain = 10;
  if (!minYDomain) minYDomain = 0;

  this.scales = {
    x: d3.time.scale().range([0, this.chart.width]).domain(domain),

    y: d3.scale.linear().range([this.chart.height, 0]).domain([minYDomain, maxYDomain]),
  };
};

Chart.prototype._setAxis = function () {
  var _this = this;

  // Calculate the amount of ticks
  var ticksY = Math.round(this.chart.height / 40);
  var ticksX = Math.round(this.chart.width / 150);

  this.axis = {
    x: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat(timeFormat.chart),

    y: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return (
          cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(d), 1) +
          " " +
          _this.options.i18n.unit
        );
      }),

    gridX: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.chart.height, 0, 0)
      .tickFormat(""),
    gridY: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY * 2)
      .tickSize(-this.chart.width, 0, 0)
      .tickFormat(""),
  };
};

Chart.prototype._setDataShapes = function () {
  var _this = this;

  this.shapes = {
    line: d3.svg
      .line()
      .x(function (d) {
        return _this.scales.x(d.timestamp);
      })
      .y(function (d) {
        let y = _this.scales.y(d.y);
        return y;
      }),
  };
};

Chart.prototype._drawClipPaths = function () {
  var defs;

  this.defs = defs = this.elements.svg.append("defs");

  this.defs.clip = defs
    .append("svg:clipPath")
    .attr("id", "clip-" + this.options.id)
    .append("svg:rect")
    .attr("x", 0)
    .attr("y", -8)
    .attr("height", this.chart.height + 8);

  this.defs.totalClip = defs
    .append("svg:clipPath")
    .attr("id", "clip-total-" + this.options.id)
    .append("svg:rect")
    .attr("x", "0")
    .attr("y", "-8")
    .attr("height", this.totalChart.height + 8);

  this.defs.mask = defs
    .append("svg:mask")
    .attr("id", "mask-" + this.options.id)
    .attr("width", this.chart.width)
    .attr("height", this.chart.height + 8)
    .attr("x", 0)
    .attr("y", -8);
};

export default Chart;
